import { cloneDeep, merge } from 'lodash'
import { deserializeMaps } from './configSerializer'
import { getDefaultConfig, backwardsFlag } from './configDefaults'
import {
    DEFAULT_CHART_PREFERENCES,
    TITLE_DEFAULT_VALUES,
} from '../../components/screens/Explorer/DrawerTabs/StyleTab/styleConsts'
import { SORTING } from '../../components/Insights/consts'

/**
 * Removes font scale factors from style overrides.
 * Part of backwards compat migration for older saved summaries.
 *
 * @param {Object} config - Config object with styleOverrides
 */
export const removeFontScaleFactors = (config) => {
    const { styleOverrides } = config
    if (!styleOverrides) return

    if (styleOverrides?.title?.textStyle?.fontScaleFactor) {
        delete styleOverrides.title.textStyle.fontScaleFactor
    }
    if (styleOverrides?.legend?.textStyle?.fontScaleFactor) {
        delete styleOverrides.legend.textStyle.fontScaleFactor
    }
    if (styleOverrides?.legend?.fontScaleFactor) {
        delete styleOverrides.legend.fontScaleFactor
    }

    styleOverrides.yAxis?.forEach((axis) => {
        if (axis?.axisLabel?.fontScaleFactor) delete axis.axisLabel.fontScaleFactor
        if (axis?.nameTextStyle?.fontScaleFactor) delete axis.nameTextStyle.fontScaleFactor
    })

    styleOverrides.xAxis?.forEach((axis) => {
        if (axis?.axisLabel?.fontScaleFactor) delete axis.axisLabel.fontScaleFactor
        if (axis?.nameTextStyle?.fontScaleFactor) delete axis.nameTextStyle.fontScaleFactor
    })

    if (styleOverrides?.pie?.label?.fontScaleFactor) {
        delete styleOverrides.pie.label.fontScaleFactor
    }
    if (styleOverrides?.donut?.label?.fontScaleFactor) {
        delete styleOverrides.donut.label.fontScaleFactor
    }
}

/**
 * Normalizes table-related config for backwards compatibility.
 * Handles old summaries that don't have selectedTableKpis.
 *
 * @param {Object} config - Config to normalize
 * @param {Object} workspaceMetadata - Workspace metadata (optional)
 */
const normalizeTableConfig = (config, workspaceMetadata) => {
    if (config.selectedTableKpis) return // Already normalized

    config.selectedTableColumns = []
    config.selectedTableKpis = []
    config.selectedTables = []
    config.tableTimeSeries = true

    if (config.view !== 'table' || !workspaceMetadata) return

    config.selectedItems?.forEach((item) => {
        // Add to selectedTables
        if (!config.selectedTables.includes(item.reportSeriesTableID)) {
            config.selectedTables.push(item.reportSeriesTableID)
        }

        // Process columns or KPIs
        if (item.columnName) {
            // Measure
            workspaceMetadata?.inboxes?.forEach((inbox) => {
                inbox.tables?.forEach((table) => {
                    if (table.reportSeriesTableID === item.reportSeriesTableID) {
                        table.columns?.forEach((col) => {
                            const isDrillAttribute = config.tableDrillAttributes?.includes(
                                col.columnName
                            )
                            if (col.columnName === item.columnName || isDrillAttribute) {
                                config.selectedTableColumns.push({
                                    ...col,
                                    reportSeriesTableID: table.reportSeriesTableID,
                                })
                            }
                        })
                    }
                })
            })
        } else if (item.kpi) {
            // KPI
            workspaceMetadata?.kpis?.forEach((kpi) => {
                if (kpi.metricName === item.kpi) {
                    config.selectedTableKpis.push(kpi)

                    workspaceMetadata?.inboxes?.forEach((inbox) => {
                        inbox.tables?.forEach((table) => {
                            if (table.reportSeriesTableID === kpi.reportSeriesTableID) {
                                table.columns?.forEach((col) => {
                                    if (config.tableDrillAttributes?.includes(col.columnName)) {
                                        config.selectedTableColumns.push({
                                            ...col,
                                            reportSeriesTableID: table.reportSeriesTableID,
                                        })
                                    }
                                })
                            }
                        })
                    })
                }
            })
        }
    })
}

/**
 * Normalizes style overrides for backwards compatibility.
 *
 * @param {Object} config - Config to normalize
 */
const normalizeStyleOverrides = (config) => {
    // Ensure styleOverrides exists
    if (!config.styleOverrides) {
        config.styleOverrides = cloneDeep(DEFAULT_CHART_PREFERENCES)
    } else {
        // Merge with defaults to fill in missing properties
        merge(config.styleOverrides, DEFAULT_CHART_PREFERENCES)
    }

    const { styleOverrides } = config

    // Ensure axes are arrays (backwards compat)
    if (!Array.isArray(styleOverrides.xAxis)) {
        styleOverrides.xAxis = [cloneDeep(styleOverrides.xAxis || DEFAULT_CHART_PREFERENCES.xAxis)]
    }
    if (!Array.isArray(styleOverrides.yAxis)) {
        styleOverrides.yAxis = [cloneDeep(styleOverrides.yAxis || DEFAULT_CHART_PREFERENCES.yAxis)]
    }

    // Handle dimensions and font scale factors
    if (!styleOverrides.dimensions) {
        if (styleOverrides.title?.textStyle?.fontScaleFactor) {
            if (config.view !== 'kpi') {
                const fontSize =
                    styleOverrides.title.textStyle.fontSize ??
                    TITLE_DEFAULT_VALUES.textStyle.fontSize
                const scaleFactor = styleOverrides.title.textStyle.fontScaleFactor
                styleOverrides.dimensions = {
                    width: fontSize / scaleFactor.x,
                    height: fontSize / scaleFactor.y,
                }
            } else {
                styleOverrides.dimensions = { width: 200, height: 200 }
            }
        } else {
            styleOverrides.dimensions =
                config.view !== 'kpi' ? { width: 900, height: 600 } : { width: 200, height: 200 }
        }
        removeFontScaleFactors(config)
    }

    // Ensure pie/donut styles
    if (!styleOverrides.pie || !styleOverrides.donut) {
        styleOverrides.pie = cloneDeep(DEFAULT_CHART_PREFERENCES.pie)
        styleOverrides.donut = cloneDeep(DEFAULT_CHART_PREFERENCES.donut)
    }
    if (!styleOverrides.pie?.itemStyle) {
        styleOverrides.pie.itemStyle = cloneDeep(DEFAULT_CHART_PREFERENCES.pie.itemStyle)
        styleOverrides.donut.itemStyle = cloneDeep(DEFAULT_CHART_PREFERENCES.donut.itemStyle)
    }
    if (!styleOverrides.pie?.label) {
        styleOverrides.pie.label = cloneDeep(DEFAULT_CHART_PREFERENCES.pie.label)
        styleOverrides.donut.label = cloneDeep(DEFAULT_CHART_PREFERENCES.donut.label)
    }
}

/**
 * Normalizes worksheet-related config for backwards compatibility.
 *
 * @param {Object} config - Config to normalize
 */
const normalizeWorksheetConfig = (config) => {
    if (!config.worksheetID || config.backwardsFlag) return

    config.backwardsFlag = backwardsFlag

    // Update selectedTableColumns
    config.selectedTableColumns =
        config.selectedTableColumns?.map((c) => {
            if (c.isMeasure && c.reportSeriesTableID?.includes('orphan')) {
                return {
                    ...c,
                    worksheetID: config.worksheetID,
                    rangeName: config.rangeName,
                }
            }
            return c
        }) || []

    // Update selectedItems
    config.selectedItems =
        config.selectedItems
            ?.map((i) => ({
                measureName: i.columnName,
                worksheetID: config.worksheetID,
                rangeName: config.rangeName,
            }))
            .filter((i) => i.measureName) || []
}

/**
 * Normalizes a saved config for backwards compatibility.
 * Handles old config formats and missing properties.
 *
 * This is the main entry point for config normalization.
 * Call this when loading a saved summary or receiving config from external sources.
 *
 * @param {Object} savedConfig - Raw saved config object
 * @param {Object} workspaceMetadata - Workspace metadata (optional, needed for table normalization)
 * @returns {Object} - Normalized config object
 */
export const normalizeSavedConfig = (savedConfig, workspaceMetadata = null) => {
    if (!savedConfig) return getDefaultConfig()

    // Clone to avoid mutating original
    const config = cloneDeep(savedConfig)

    // Deserialize Maps (seriesTypeMap, selectedDates)
    const deserialized = deserializeMaps(config)
    Object.assign(config, deserialized)

    // Ensure required arrays exist
    if (!config.usedDrillAttributes) config.usedDrillAttributes = []
    if (!config.worksheetColumns) config.worksheetColumns = []
    if (!config.sortColumns) config.sortColumns = [{ column: '', order: SORTING.NAT }]
    if (!config.metricsOrder) config.metricsOrder = []
    if (!config.selectedItems) config.selectedItems = []

    // Ensure display rows
    if (!config.displayRows) config.displayRows = 50

    // KPI compare settings
    if (!config.kpiCompareType) {
        config.kpiCompareType = 'percentage'
        config.kpiCompareTarget = undefined
    }

    // Table state
    if (!config.tableState) {
        config.tableState = {
            nonPivotState: { rowGroupCols: [] },
            pivotState: { rowGroupCols: [], pivotCols: [] },
            pinnedCols: [],
        }
    }

    // Table time series
    if (config.tableTimeSeries === undefined) {
        config.tableTimeSeries = true
    }

    // Normalize sub-configs
    normalizeTableConfig(config, workspaceMetadata)
    normalizeStyleOverrides(config)
    normalizeWorksheetConfig(config)

    return config
}

/**
 * Parses a saved summary object from the API.
 * Handles JSON parsing and normalization.
 *
 * @param {Object} savedObject - Object with savedObject JSON string
 * @param {Object} workspaceMetadata - Workspace metadata (optional)
 * @returns {Object|null} - Parsed and normalized config, or null if invalid
 */
export const parseSavedSummary = (savedObject, workspaceMetadata = null) => {
    if (!savedObject?.savedObject) return null

    try {
        const parsed = JSON.parse(savedObject.savedObject)
        return normalizeSavedConfig(parsed, workspaceMetadata)
    } catch (error) {
        console.error('Failed to parse saved summary:', error)
        return null
    }
}
